import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useBillingForm } from '../hooks/useBillingForm';
import RoyaltyDetailModal from './RoyaltyDetailModal';
import PRSDetailModal from './PRSDetailModal';
import MailWizard from './MailWizard';
import { SOCIETIES, SOCIETY_FIELDS, commissionSummary } from '../utils/clientProfile';

// Societies entered as one amount (IPRS and PRS have their own receipt lines),
// in the order the form shows them.
const SINGLE_AMOUNT_SOCIETIES = ['Sound Exchange', 'ISAMRA', 'ASCAP', 'BMI', 'SOCAN', 'PPL', 'MLC', 'IMRO'];

const shortDate = (value) => (value
  ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
};

const monthLabels = {
  apr: 'April', may: 'May', jun: 'June', jul: 'July',
  aug: 'August', sep: 'September', oct: 'October', nov: 'November',
  dec: 'December', jan: 'January', feb: 'February', mar: 'March',
};

function BillingForm() {
  const { selectedClient, currentMonth, settings, showToast } = useApp();
  const {
    formData,
    calculations,
    isPerSociety,
    rateFor,
    isUnlocked,
    unlockCommission,
    lockCommission,
    setTypedCommission,
    societyAccess,
    carryForwardLocked,
    carryForwardOverride,
    unlockCarryForward,
    handleInputChange,
    setSocietyDate,
    clearForm,
    handleSaveAsDraft,
    handleSubmit,
    handleDelete,
    status,
    isReadOnly,
    enableEdit,
    beforeJoining,
    joinedIn,
    updateIprsEntries,
    updatePrsEntries,
  } = useBillingForm();

  const [iprsModalOpen, setIprsModalOpen] = useState(false);
  const [prsModalOpen, setPrsModalOpen] = useState(false);
  // Submitting opens the mail wizard; nothing is mailed unless it is walked through.
  const [mailOpen, setMailOpen] = useState(false);

  const { financialYear } = settings;
  const year = ['jan', 'feb', 'mar'].includes(currentMonth)
    ? financialYear.endYear
    : financialYear.startYear;
  const monthLabel = `${monthLabels[currentMonth]} ${year}`;

  // A society box is open only when the client is signed to that society (or it
  // already holds money from before the client's society list was tidied).
  // Closed boxes say why rather than silently ignoring a click.
  const notAMember = (society) => showToast(
    `${selectedClient?.name || 'This client'} is not a member of ${society}. Add it in Client Master to enter an amount.`,
    'error'
  );

  // type="number" boxes change value when the wheel scrolls over a focused one,
  // which was quietly turning amounts into 1 or 2 while scrolling the page. One
  // handler on the form covers every box; the spinner arrows are hidden in CSS.
  const blurNumberOnWheel = () => {
    const el = document.activeElement;
    if (el && el.type === 'number') el.blur();
  };

  // With one rate for everything, say it once. With per-society rates, name each
  // one the client actually holds - a single "15%" was misleading.
  const memberList = SOCIETIES
    .filter((s) => societyAccess[s]?.isMember);
  const unlockedList = SOCIETIES.filter((s) => isUnlocked(s));
  const commissionSubtitle = [
    isPerSociety && memberList.length
      ? `Auto-calculated at ${memberList.filter((s) => !isUnlocked(s)).map((s) => `${s} ${rateFor(s)}%`).join(', ') || 'client master rates'}`
      : `Auto-calculated at ${formData.commissionRate || 0}% commission rate`,
    unlockedList.length ? `typed by hand this month: ${unlockedList.join(', ')}` : '',
  ].filter(Boolean).join(' \u00b7 ');

  // One society's commission box. Normally calculated and read-only; unlocked,
  // it is typed by hand for this month only and the calculation leaves it alone.
  const renderCommission = (society) => {
    const key = SOCIETY_FIELDS[society].commission;
    const unlocked = isUnlocked(society);
    const canUnlock = !isReadOnly && (unlocked || societyAccess[society]?.editable);
    return (
      <div className={`input-group ${unlocked ? 'commission-unlocked' : 'calculated'}`} key={society}>
        <label>
          {society} Commission
          {unlocked
            ? <span className="rate-chip unlocked-chip" title={`Client Master rate is ${rateFor(society)}%`}>typed this month</span>
            : isPerSociety && <span className="rate-chip">{rateFor(society)}%</span>}
          {canUnlock && (
            <button
              type="button"
              className={`commission-lock-btn${unlocked ? ' is-unlocked' : ''}`}
              onClick={() => (unlocked ? lockCommission(society) : unlockCommission(society))}
              title={unlocked
                ? `Lock again: back to ${rateFor(society)}% from Client Master`
                : 'Type this commission by hand for this month only'}
            >
              {unlocked ? 'Lock' : 'Unlock'}
            </button>
          )}
        </label>
        <div className="input-prefix"><span>&#8377;</span>
          {unlocked && !isReadOnly ? (
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.commissionOverrides?.[society] ?? ''}
              onChange={(e) => setTypedCommission(society, e.target.value)}
              placeholder="0.00"
            />
          ) : (
            <input type="number" value={(calculations[key] || 0).toFixed(2)} readOnly />
          )}
        </div>
      </div>
    );
  };

  const amountsSubtitle = societyAccess.noProfile
    ? 'No societies are set for this client, so every box is open. Set them in Client Master to narrow this down.'
    : `Only this client's societies can be filled in: ${memberList.join(', ')}`;

  const renderSocietyAmount = (society) => {
    const access = societyAccess[society] || {};
    const { amount, label } = SOCIETY_FIELDS[society];
    const blocked = !access.editable;
    return (
      <div
        key={society}
        className={`input-group${blocked ? ' society-blocked' : ''}`}
        onClick={blocked && !isReadOnly ? () => notAMember(society) : undefined}
        title={blocked ? `Not a member of ${society}` : undefined}
      >
        <label>
          {label}
          {access.required && <span style={{ color: '#ef4444' }}> *</span>}
          {blocked && <span className="not-member-tag">not a member</span>}
          {access.strayAmount && <span className="stray-tag">not in society list</span>}
        </label>
        <div className="input-prefix"><span>&#8377;</span>
          <input
            type="number"
            name={amount}
            value={formData[amount]}
            onChange={handleInputChange}
            placeholder={blocked ? '\u2014' : '0.00'}
            disabled={isReadOnly || blocked}
          />
        </div>
        {!blocked && renderSocietyDates(society)}
      </div>
    );
  };

  // When the royalty came in, and when the email about it arrived.
  const renderSocietyDates = (society) => {
    const d = formData.societyDates?.[society] || {};
    if (isReadOnly) {
      if (!d.receivedDate && !d.emailDate) return null;
      return (
        <div className="society-dates is-read">
          <span>Received <b>{shortDate(d.receivedDate)}</b></span>
          <span>Email <b>{shortDate(d.emailDate)}</b></span>
        </div>
      );
    }
    return (
      <div className="society-dates">
        <label>
          <span>Received on</span>
          <input type="date" value={d.receivedDate || ''} onChange={(e) => setSocietyDate(society, 'receivedDate', e.target.value)} />
        </label>
        <label>
          <span>Email received</span>
          <input type="date" value={d.emailDate || ''} onChange={(e) => setSocietyDate(society, 'emailDate', e.target.value)} />
        </label>
      </div>
    );
  };

  const onSaveDraft = async () => {
    try { await handleSaveAsDraft(); } catch (error) {
      // Only show toast for local validation errors (not API errors — AppContext handles those)
      if (!error.response) showToast(error.message, 'error');
    }
  };

  const onSubmit = async () => {
    try {
      await handleSubmit();
      setMailOpen(true);
    } catch (error) {
      if (!error.response) showToast(error.message, 'error');
    }
  };

  const onClear = async () => {
    if (!selectedClient) { showToast('Please select a client first', 'warning'); return; }
    if (window.confirm('Are you sure you want to clear all form data?')) {
      try {
        if (status) { await handleDelete(); } else { clearForm(); }
        showToast('Form cleared!');
      } catch (error) { showToast(error.message, 'error'); }
    }
  };

  if (!selectedClient) {
    return (
      <main className="data-form">
        <div className="empty-state" style={{ padding: '80px 20px' }}>
          <h3>Select a Client</h3>
          <p>Choose a client from the panel on the left to begin entering royalty accounting data</p>
        </div>
      </main>
    );
  }

  return (
    <main className="data-form" onWheel={blurNumberOnWheel}>
      {/* Form Header */}
      <div className="form-header">
        <div className="form-header-info">
          <h2>
            {selectedClient.name}
            {status && (
              <span className={`status-indicator ${status === 'submitted' ? 'submitted' : 'draft'}`}>
                <span className="dot"></span>
                {status === 'submitted' ? 'Submitted' : 'Draft'}
              </span>
            )}
          </h2>
          <span>{selectedClient.clientId} • Commission: {commissionSummary(selectedClient)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isReadOnly && !beforeJoining && (
            <button className="btn btn-warning" onClick={enableEdit}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Edit Entry
            </button>
          )}
          <div className="month-badge">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {monthLabel}
          </div>
        </div>
      </div>

      {beforeJoining && (
        <div className="joining-lock" role="status">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>
            {selectedClient.name} joined in <strong>{joinedIn}</strong>. {monthLabel} is before that, so it is locked.
            Entries start in {joinedIn}, opening with the client&rsquo;s previous balance.
          </span>
        </div>
      )}

      <div className="form-body">
        {/* Configurable Rates Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="5" x2="5" y2="19"></line>
                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                <circle cx="17.5" cy="17.5" r="2.5"></circle>
              </svg>
            </div>
            <div>
              <div className="section-title">Configurable Rates</div>
              <div className="section-subtitle">Commission rate, GST rate and royalty type come from the client record</div>
            </div>
          </div>
          <div className="input-grid">
            <div className="input-group calculated">
              <label>Commission Rate (%)</label>
              <div className="input-prefix">
                <span>%</span>
                <input
                  type="number"
                  name="commissionRate"
                  value={formData.commissionRate}
                  readOnly
                />
              </div>
            </div>
            <div className="input-group calculated">
              <label>GST Rate (%)</label>
              <div className="input-prefix">
                <span>%</span>
                <input
                  type="number"
                  name="gstRate"
                  value={formData.gstRate}
                  readOnly
                />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Royalty Type</label>
              <input
                type="text"
                name="royaltyType"
                value={formData.royaltyType}
                readOnly
                title="Set on the client record - edit it under Reports - Client Master"
              />
            </div>
          </div>
        </div>

        {/* Royalty Amounts Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div>
              <div className="section-title">Royalty Amounts</div>
              <div className="section-subtitle">{amountsSubtitle}</div>
            </div>
          </div>
          <div className="input-grid">
            {formData.iprsEntries?.length > 0 ? (
              <div className="input-group royalty-clickable" onClick={() => setIprsModalOpen(true)}>
                <label>IPRS Amount{societyAccess['IPRS']?.required && <span style={{ color: '#ef4444' }}> *</span>}
                  <span className="click-hint">Click to view details</span>
                </label>
                <div className="input-prefix"><span>&#8377;</span>
                  <input type="number" name="iprsAmount" value={formData.iprsAmount} readOnly placeholder="0.00" style={{ cursor: 'pointer' }} />
                </div>
                <span className="entries-badge">{formData.iprsEntries.length} {formData.iprsEntries.length === 1 ? 'entry' : 'entries'}</span>
              </div>
            ) : (
              <div className="input-group" style={{ position: 'relative' }}>
                <label>IPRS Amount{societyAccess['IPRS']?.required && <span style={{ color: '#ef4444' }}> *</span>}{!societyAccess['IPRS']?.editable && <span className="not-member-tag">not a member</span>}{societyAccess['IPRS']?.strayAmount && <span className="stray-tag">not in society list</span>}</label>
                <div className="input-prefix"><span>&#8377;</span>
                  <input type="number" name="iprsAmount" value={formData.iprsAmount} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly || !societyAccess['IPRS']?.editable} />
                </div>
                {!isReadOnly && societyAccess['IPRS']?.editable && (
                  <button type="button" className="detail-open-btn" onClick={() => setIprsModalOpen(true)} title="Add detailed entries">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                )}
              </div>
            )}
            {formData.prsEntries?.length > 0 ? (
              <div className="input-group royalty-clickable" onClick={() => setPrsModalOpen(true)}>
                <label>PRS Amount (INR){societyAccess['PRS']?.required && <span style={{ color: '#ef4444' }}> *</span>}
                  <span className="click-hint">Click to view details</span>
                </label>
                <div className="input-prefix"><span>&#8377;</span>
                  <input type="number" name="prsAmount" value={formData.prsAmount} readOnly placeholder="0.00" style={{ cursor: 'pointer' }} />
                </div>
                <span className="entries-badge">{formData.prsEntries.length} {formData.prsEntries.length === 1 ? 'entry' : 'entries'}</span>
              </div>
            ) : (
              <div className="input-group" style={{ position: 'relative' }}>
                <label>PRS Amount (INR){societyAccess['PRS']?.required && <span style={{ color: '#ef4444' }}> *</span>}{!societyAccess['PRS']?.editable && <span className="not-member-tag">not a member</span>}{societyAccess['PRS']?.strayAmount && <span className="stray-tag">not in society list</span>}</label>
                <div className="input-prefix"><span>&#8377;</span>
                  <input type="number" name="prsAmount" value={formData.prsAmount} onChange={handleInputChange} placeholder="0.00" step="0.01" disabled={isReadOnly || !societyAccess['PRS']?.editable} />
                </div>
                {!isReadOnly && societyAccess['PRS']?.editable && (
                  <button type="button" className="detail-open-btn" onClick={() => setPrsModalOpen(true)} title="Add detailed entries">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  </button>
                )}
              </div>
            )}
            {SINGLE_AMOUNT_SOCIETIES.map(renderSocietyAmount)}
          </div>
        </div>

        {/* Commission Section (Auto-Calculated) */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div>
              <div className="section-title">Commission Calculation</div>
              <div className="section-subtitle">{commissionSubtitle}</div>
            </div>
          </div>
          <div className="input-grid">
            {['IPRS', 'PRS', ...SINGLE_AMOUNT_SOCIETIES].map(renderCommission)}
            <div className="input-group calculated" style={{ gridColumn: 'span 2' }}>
              <label>Total Commission</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.totalCommission.toFixed(2)} readOnly style={{ fontWeight: 700 }} />
              </div>
            </div>
          </div>
        </div>

        {/* GST & Invoice Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <div>
              <div className="section-title">GST & Invoice</div>
              <div className="section-subtitle">Current month and previous outstanding GST calculations</div>
            </div>
          </div>
          <div className="input-grid">
            <div className="input-group">
              <label>Current Month GST Base</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="currentMonthGstBase" value={formData.currentMonthGstBase} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Current Month GST ({formData.gstRate || 18}%)</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.currentMonthGst.toFixed(2)} readOnly />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Current Month Invoice Total</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.currentMonthInvoiceTotal.toFixed(2)} readOnly />
              </div>
            </div>
            <div className="input-group">
              <label>Previous Outstanding GST Base</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="previousOutstandingGstBase" value={formData.previousOutstandingGstBase} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Previous Outstanding GST ({formData.gstRate || 18}%)</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.previousOutstandingGst.toFixed(2)} readOnly />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Previous Outstanding Invoice Total</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.previousOutstandingInvoiceTotal.toFixed(2)} readOnly />
              </div>
            </div>
          </div>
        </div>

        {/* Receipts & TDS Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div>
              <div className="section-title">Receipts & TDS</div>
              <div className="section-subtitle">Current month and previous month payments</div>
            </div>
          </div>
          <div className="input-grid">
            <div className="input-group">
              <label>Current Month Receipt</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="currentMonthReceipt" value={formData.currentMonthReceipt} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
            <div className="input-group">
              <label>Current Month TDS</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="currentMonthTds" value={formData.currentMonthTds} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
            <div className="input-group">
              <label>Previous Month Receipt</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="previousMonthReceipt" value={formData.previousMonthReceipt} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
            <div className="input-group">
              <label>Previous Month TDS</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="previousMonthTds" value={formData.previousMonthTds} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
            </div>
          </div>
        </div>

        {/* Outstanding Tracking Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div>
              <div className="section-title">Outstanding Tracking</div>
              <div className="section-subtitle">Auto-calculated outstanding amounts with carry-forward</div>
            </div>
          </div>
          <div className="input-grid">
            <div className={`input-group${carryForwardLocked && !carryForwardOverride ? ' calculated' : ''}`}>
              <label>
                Previous Month Outstanding (Carry-Forward)
                {carryForwardLocked && !carryForwardOverride && (
                  <>
                    <span className="not-member-tag">from last month</span>
                    {!isReadOnly && (
                      <button type="button" className="cf-link carry-override" onClick={unlockCarryForward}>
                        Override
                      </button>
                    )}
                  </>
                )}
                {carryForwardOverride && <span className="stray-tag">overridden</span>}
              </label>
              <div className="input-prefix"><span>&#8377;</span>
                <input
                  type="number"
                  name="previousMonthOutstanding"
                  value={formData.previousMonthOutstanding}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  readOnly={carryForwardLocked && !carryForwardOverride}
                  disabled={isReadOnly}
                  title={carryForwardLocked && !carryForwardOverride
                    ? 'Carried forward from the previous month. Use Override to set a different opening balance.'
                    : undefined}
                />
              </div>
            </div>
            <div className="input-group">
              <label>Extra Amount <span style={{color:'#ef4444'}}>*</span></label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" name="extraAmount" value={formData.extraAmount} onChange={handleInputChange} placeholder="0.00" disabled={isReadOnly} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Enter amount to adjust outstanding (e.g. 500 to reduce, -12 to increase)
              </span>
            </div>
            <div className="input-group calculated">
              <label>Invoice Pending (Current Month)</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.invoicePendingCurrentMonth.toFixed(2)} readOnly />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Previous Invoice Pending</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.previousInvoicePending.toFixed(2)} readOnly />
              </div>
            </div>
            <div className="input-group calculated">
              <label>Monthly Outstanding</label>
              <div className="input-prefix"><span>&#8377;</span>
                <input type="number" value={calculations.monthlyOutstanding.toFixed(2)} readOnly style={{ fontWeight: 700 }} />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="summary-card">
          <div className="summary-header">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <h3>Accounting Summary</h3>
          </div>
          <div className="summary-grid">
            <div className="summary-item">
              <div className="label">Total Commission</div>
              <div className="value">{formatCurrency(calculations.totalCommission)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Current Month Invoice</div>
              <div className="value">{formatCurrency(calculations.currentMonthInvoiceTotal)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Monthly Outstanding</div>
              <div className="value">{formatCurrency(calculations.monthlyOutstanding)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Total Outstanding</div>
              <div className="value" style={{ color: calculations.totalOutstanding < 0 ? '#f04040' : '#92d050', fontSize: 18 }}>
                {formatCurrency(calculations.totalOutstanding)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        {!isReadOnly ? (
          <>
            <button className="btn btn-danger" onClick={onClear}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Clear Form
            </button>
            <button className="btn btn-secondary" onClick={onSaveDraft}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Save Draft
            </button>
            <button className="btn btn-success" onClick={onSubmit}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Submit Entry
            </button>
          </>
        ) : beforeJoining ? (
          <span style={{ color: 'var(--text-secondary)', fontSize: 14, marginRight: 'auto', alignSelf: 'center' }}>
            Locked &mdash; before {selectedClient.name} joined in {joinedIn}.
          </span>
        ) : (
          <>
            <span style={{ color: 'var(--text-secondary)', fontSize: 14, marginRight: 'auto', alignSelf: 'center' }}>
              This entry has been submitted. Click "Edit Entry" above to make changes.
            </span>
            <button className="btn btn-primary" onClick={() => setMailOpen(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                <path d="m22 7-10 6L2 7"></path>
              </svg>
              Send Mail
            </button>
          </>
        )}
      </div>

      {/* IPRS Detail Modal */}
      {iprsModalOpen && (
        <RoyaltyDetailModal
          title="IPRS"
          entries={formData.iprsEntries || []}
          commissionRate={rateFor('IPRS')}
          typedCommission={isUnlocked('IPRS') ? calculations.iprsCommission : null}
          isFormReadOnly={isReadOnly}
          fallbackAmount={formData.iprsAmount}
          onSave={(entries, totalAmount, totalCommission, typed) => {
            updateIprsEntries(entries, totalAmount);
            // Unlocked in the popup: the lines' typed commissions are the month's.
            if (typed != null) setTypedCommission('IPRS', typed);
            else if (isUnlocked('IPRS')) lockCommission('IPRS');
            setIprsModalOpen(false);
          }}
          onClose={() => setIprsModalOpen(false)}
        />
      )}

      {/* PRS Detail Modal */}
      {prsModalOpen && (
        <PRSDetailModal
          title="PRS"
          entries={formData.prsEntries || []}
          commissionRate={rateFor('PRS')}
          typedCommission={isUnlocked('PRS') ? calculations.prsCommission : null}
          isFormReadOnly={isReadOnly}
          fallbackGbp={formData.prsGbp}
          fallbackRate={formData.gbpToInrRate}
          fallbackAmount={formData.prsAmount}
          onSave={(entries, totalAmount, totalCommission, typed) => {
            updatePrsEntries(entries, totalAmount);
            // Unlocked in the popup: the lines' typed commissions are the month's.
            if (typed != null) setTypedCommission('PRS', typed);
            else if (isUnlocked('PRS')) lockCommission('PRS');
            setPrsModalOpen(false);
          }}
          onClose={() => setPrsModalOpen(false)}
        />
      )}

      {mailOpen && (
        <MailWizard
          clientId={selectedClient.clientId}
          month={currentMonth}
          year={year}
          onClose={() => setMailOpen(false)}
        />
      )}
    </main>
  );
}

export default BillingForm;
